import { CaretUpOutlined } from '@ant-design/icons';
import { Button, Dropdown, Grid } from 'antd';
import React from 'react';

type VersionCode = 'production' | 'staging' | 'testing' | 'development' | 'preview' | 'release';

export type Version = {
  code: VersionCode;
  name: string;
  color: string;
  url: string;
  show: boolean;
};

function getAppNameFromUrl(url: string) {
  const parts = url.split('/');
  const indexOfWebapps = parts.indexOf('webapps');
  if (indexOfWebapps === -1) {
    return '';
  } else {
    return parts[indexOfWebapps + 1];
  }
}

function getAppVersionFromUrl(url: string) {
  const parts = url.split('/');
  const indexOfWebapps = parts.indexOf('webapps');
  if (indexOfWebapps === -1) {
    return '';
  } else {
    return parts[indexOfWebapps + 2];
  }
}

function isPreviewVersion(version: string) {
  return version.startsWith('preview-') && version.length > 8;
}

function isReleaseVersion(version: string) {
  return version.startsWith('v') && version.length > 1;
}

function getAppVersionCode(version: string) {
  if (isPreviewVersion(version)) {
    return 'preview';
  } else if (isReleaseVersion(version)) {
    return 'release';
  } else {
    return version;
  }
}

function getCurrentAppPath(url: string) {
  const parts = url.split('/');
  const indexOfWebapps = parts.indexOf('webapps');
  if (indexOfWebapps === -1) {
    return '';
  } else {
    return parts.slice(indexOfWebapps + 3).join('/');
  }
}

export default function VersionSelector() {
  const appName = getAppNameFromUrl(window.location.href);
  const appVersion = getAppVersionFromUrl(window.location.href); // development, staging, preview-123, v1.0.0
  const appVersionCode = getAppVersionCode(appVersion); // development, staging, preview, release
  const { md } = Grid.useBreakpoint();

  const versions: Version[] = [
    {
      code: 'development',
      name: 'Development Version',
      color: 'red',
      url: `/webapps/${appName}/development/${getCurrentAppPath(window.location.href)}`,
      show: process.env.NODE_ENV === 'development',
    },
    {
      code: 'testing',
      name: 'Testing Version',
      color: 'orange',
      url: `/webapps/${appName}/testing/${getCurrentAppPath(window.location.href)}`,
      show: true,
    },
    {
      code: 'staging',
      name: 'Staging Version',
      color: 'yellow',
      url: `/webapps/${appName}/staging/${getCurrentAppPath(window.location.href)}`,
      show: true,
    },
    {
      code: 'production',
      name: 'Production Version',
      color: 'green',
      url: `/webapps/${appName}/production/${getCurrentAppPath(window.location.href)}`,
      show: true,
    },
    {
      code: 'preview',
      name: 'Preview Version',
      color: 'grey',
      url: `/`,
      show: false,
    },
    {
      code: 'release',
      name: 'Release Version',
      color: 'grey',
      url: `/`,
      show: false,
    },
  ];

  const currentVersion = versions.find((v) => v.code === appVersionCode);
  const isProd = currentVersion?.code === 'production';

  if (!currentVersion) {
    return null;
  }

  return (
    <Dropdown
      placement="top"
      arrow
      menu={{
        items: versions
          .filter((v) => v.show)
          .map((version) => ({
            key: version.name,
            label: (
              <span>
                <i className={`dot ${version.color}`} /> {version.name}
              </span>
            ),
            onClick: () => {
              window.location.href = version.url;
            },
          })),
      }}
    >
      <Button
        type="text"
        style={{
          width: isProd ? 32 : 'initial',
          padding: isProd ? 0 : 'revert',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        icon={<i className={`dot ${currentVersion?.color}`} />}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!isProd && md && (
            <>
              <span style={{ marginInline: 5 }}>
                You are on <b>{currentVersion.name}</b>
              </span>
              <CaretUpOutlined />
            </>
          )}
        </div>
      </Button>
    </Dropdown>
  );
}
